import update from 'immutability-helper';
import deepCopy from 'lodash/cloneDeep';
import {
  ICalculationSession,
  ICalculationStep,
  ICurrentCalculationSession,
  IFlowOverrideField,
  IFlowOverrides,
  IFlows,
  IOverrideFilterInfo,
} from './CurrentCalculationSessionInterface';
import { getFlowTypeFromFunctionName } from '../../Components/Calculations/utils';
import ShortUniqueId from 'short-unique-id';

const calculationState: ICalculationSession = {
  properties: {
    '1.1': {
      currentCalculationSession: null,
    },
  },
  propList: { 1: ['1.1'] },
  savedCalculations: [],
  fetchRowLevelCalculations: false,
};

const CalculationReducer = (
  state: ICalculationSession = calculationState,
  action: any,
): ICalculationSession => {
  const uid = new ShortUniqueId({ length: 4 });

  const findIndex = (propKey: string, flowId: string, conditionUid: string): number => {
    const currentCalculationSession = state.properties[propKey].currentCalculationSession;
    if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return -1;
    const flow = currentCalculationSession.calculationInfo.flows[flowId];
    const conditionIndex = flow.findIndex((flow: any) => flow.flowId === conditionUid);
    return conditionIndex;
  };

  const findInfo = (
    propKey: string,
    flowId: string,
    conditionUid: string,
  ): null | { conditionIndex: number; info: ICalculationStep } => {
    const currentCalculationSession = state.properties[propKey].currentCalculationSession;
    if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return null;
    const flow = currentCalculationSession.calculationInfo.flows[flowId];
    const conditionIndex = flow.findIndex((flow: any) => flow.flowId === conditionUid);
    const condition = flow[conditionIndex];
    return {
      conditionIndex,
      info: condition,
    };
  };

  switch (action.type) {
    case 'RESET_CALCULATION_STATE':
      return calculationState;

    case 'SET_CALCULATION_STATE': {
      const { calculations } = action.payload;
      if (!calculations) return state;
      return update(state, { $set: calculations });
    }

    case 'ADD_NEW_CALCULATION_SESSION': {
      const calculationName: string = action.payload.calculationName;
      const propKey: string = action.payload.propKey;
      const datasetId: string = action.payload.datasetId;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              currentCalculationSession: {
                name: calculationName,
                calculationInfo: {
                  selectedResultType: {},
                  flows: {},
                  fields: {},
                  conditionFilters: {},
                  calculatedFieldName: null,
                  displayFieldName: null,
                  overrideCount: 0,
                },
                flowPositions: {},
                activeFlow: null,
                activeFlowType: null,
                activeCondition: null,
                flowDisplayNames: {},
                datasetId: datasetId,
                flowAggregations: {},
                flowParameters: {},
              },
            },
          },
        },
        savedCalculations: {
          $set: state.savedCalculations ? state.savedCalculations : [],
        },
        fetchRowLevelCalculations: {
          $set: false,
        },
      });

      return newState;
    }

    case 'RESET_CALCULATION_SESSION': {
      // when the user presses close button in the calculation session

      const propKey = action.payload.propKey;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: { $set: null },
          },
        },
      });

      return newState;
    }

    case 'RESET_ALL_CALCULATION_SESSION': {
      // when the user presses silzila logo/home icon on the top left corner

      // const propKey = action.payload.propKey;

      // const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      // if (!currentCalculationSession) return state;

      const newState = update(state, {
        properties: {
          $set: {
            '1.1': {
              currentCalculationSession: null,
            },
          },
        },
        propList: {
          $set: { 1: ['1.1'] },
        },
        savedCalculations: { $set: [] },
      });

      return newState;
    }

    case 'ADD_INITIAL_CALCULATION_FLOW': {
      const propKey = action.payload.propKey;
      const calculationFlow = action.payload.calculationFlow;
      const conditionUid = action.payload.conditionUid;
      const flowDisplayName = action.payload.flowDisplayName;
      const isAggregation = calculationFlow.isAggregation;

      const calculationSession = deepCopy(state.properties[propKey].currentCalculationSession);
      let defaultCondition: string | null;
      if (!calculationSession) return state;
      if (calculationFlow.flow === 'IfElse') {
        defaultCondition = 'if';
      } else {
        defaultCondition = null;
      }

      if (calculationSession && calculationSession.calculationInfo) {
        const theUId = calculationFlow.uid;
        const theFlow = calculationFlow.flow;

        if (
          calculationSession.calculationInfo.conditionFilters &&
          calculationFlow.flow === 'IfElse'
        ) {
          calculationSession.calculationInfo.conditionFilters[conditionUid] = [
            {
              shouldAllConditionsMatch: false,
              lastDroppedEvaluationUid: null,
              conditions: [],
            },
          ];
        }

        calculationSession.calculationInfo.flows[theUId] = [
          {
            flowId: uid(),
            flow: theFlow,
            sourceType: [],
            source: [],
            isAggregation: theFlow === 'IfElse' ? false : isAggregation,
            isValid: false,
            aggregation: [],
            filter: theFlow === 'IfElse' ? conditionUid : null,
            condition: defaultCondition,
            conditionName: theFlow === 'IfElse' ? `Condition 1` : '',
            resultantDatatype: null,
            parameter: [], // array of connected parameters
            ...(theFlow === 'summarize'
              ? {
                overrided: true,
                flowOverride: {
                  dimension: {
                    type: 'honorAll',
                    dimensions: [],
                  },
                  filter: {
                    type: 'honorAll',
                    filters: [],
                    isFilterGroup: false,
                    filterGroups: [],
                    filterPanel: {
                      panelName: 'overrideFilter',
                      shouldAllConditionsMatch: true,
                      filters: [],
                    },
                  },
                  windowFunction: null,
                } as IFlowOverrides,
              }
              : {
                overrided: false,
              }),
          },
        ];

        calculationSession.activeFlow = theUId;
        calculationSession.activeFlowType = getFlowTypeFromFunctionName(calculationFlow.flow);
        calculationSession.activeCondition = 0;
        calculationSession.flowDisplayNames = {
          ...calculationSession.flowDisplayNames,
          [calculationFlow.uid]: flowDisplayName ?? calculationFlow.flow,
        };
        calculationSession.flowAggregations = {
          ...calculationSession.flowAggregations,
          [calculationFlow.uid]: isAggregation,
        };
      }

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              $set: calculationSession,
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_ACTIVE_CONDITION_ID': {
      const { propKey, activeConditionId } = action.payload;
      const calculationSession = state.properties[propKey].currentCalculationSession;
      if (calculationSession) {
        calculationSession.activeCondition = activeConditionId;
      }
      return {
        ...state,
        properties: {
          ...state.properties,
          [propKey]: {
            currentCalculationSession: calculationSession,
          },
        },
      };
    }

    case 'ADD_CONDITION_FOR_IF_ELSE': {
      const { propKey, conditionType, calculationFlow, conditionUid } = action.payload;
      const calculationSession = state.properties[propKey].currentCalculationSession;

      if (calculationSession && calculationSession.calculationInfo) {
        const theUId = calculationFlow.uid;
        const theFlow = calculationFlow.flow;
        if (calculationSession.calculationInfo.conditionFilters) {
          calculationSession.calculationInfo.conditionFilters[conditionUid] = [
            {
              shouldAllConditionsMatch: false,
              lastDroppedEvaluationUid: null,
              conditions: [],
            },
          ];
        }

        const count =
          calculationSession.calculationInfo.flows[theUId].filter(
            (flow) => flow.condition !== 'else',
          ).length + 1;

        calculationSession.calculationInfo.flows[theUId].push({
          flowId: uid(),
          flow: theFlow,
          sourceType: [],
          source: [],
          isAggregation: false,
          aggregation: [],
          filter: conditionUid,
          condition: conditionType.charAt(0).toLowerCase() + conditionType.slice(1),
          conditionName: `Condition ${count}`,
          isValid: false,
          resultantDatatype: null,
          overrided: false,
          parameter: [], // array of connected parameters
        });
        const activeConditionId =
          Object.keys(calculationSession.calculationInfo.flows[theUId])?.length - 1;
        calculationSession.activeFlow = theUId;
        calculationSession.activeCondition =
          calculationFlow.flow === 'IfElse' ? (activeConditionId ?? 0) : 0;
      }
      return {
        ...state,
        properties: {
          ...state.properties,
          [propKey]: {
            currentCalculationSession: calculationSession,
          },
        },
      };
    }

    case 'UPDATE_RESULTANT_DATATYPE': {
      const { resultantDatatype, propKey, calculationFlow, conditionNumber } = action.payload;

      // Get the current value from the state
      const currentResultantDatatype =
        state.properties[propKey]?.currentCalculationSession?.calculationInfo?.flows[
          calculationFlow.uid
        ]?.[conditionNumber === null ? 0 : conditionNumber]?.resultantDatatype;

      // Only update if the value has changed
      if (currentResultantDatatype === resultantDatatype) {
        return state;
      }

      // Proceed with the state update if the value is different
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlow.uid]: {
                    [conditionNumber === null ? 0 : conditionNumber]: {
                      resultantDatatype: { $set: resultantDatatype },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'ADD_SOURCE_INFO_IN_CALCULATION_FLOW': {
      // tableId is source here. Change it to source. tableId is a bad variable name
      const sourceId = action.payload.sourceId;
      const dataType = action.payload.dataType;
      const calculationFlow = action.payload.calculationFlow;

      // TODO: isAggregation is of no use here. Shoud be removed
      // const isAggregation = action.payload.isAggregation;
      const aggregation = action.payload.aggregation;
      const propKey = action.payload.propKey;
      const conditionNumber = action.payload.conditionNumber;

      const calculationSession = state.properties[propKey].currentCalculationSession;

      if (!calculationSession || !calculationSession.calculationInfo) return state;

      // flow type: replace
      if (calculationFlow.flow === 'replace' && dataType === 'field') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          $apply: (source: any) => [sourceId, ...source],
                        },
                        sourceType: {
                          $apply: (sourceType: any) => [dataType, ...sourceType],
                        },
                        aggregation: {
                          $apply: (agg: any) => [aggregation, ...agg],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (calculationFlow.flow === 'split' && dataType === 'field') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          $apply: (source: any) => [sourceId, ...source],
                        },
                        sourceType: {
                          $apply: (sourceType: any) => [dataType, ...sourceType],
                        },
                        aggregation: {
                          $apply: (agg: any) => [aggregation, ...agg],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (
        (calculationFlow.flow === 'substringleft' || calculationFlow.flow === 'substringright') &&
        dataType === 'field'
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          $apply: (source: any) => [sourceId, ...source],
                        },
                        sourceType: {
                          $apply: (sourceType: any) => [dataType, ...sourceType],
                        },
                        aggregation: {
                          $apply: (agg: any) => [aggregation, ...agg],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (calculationFlow.flow === 'concat' && dataType === 'text') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          $push: [sourceId],
                        },
                        sourceType: {
                          $push: [dataType],
                        },
                        aggregation: {
                          $push: [aggregation],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // flow type: log
      if (
        calculationFlow.flow === 'log' &&
        calculationSession.calculationInfo.flows[calculationFlow.uid][0].sourceType[0] === 'decimal'
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          $unshift: [sourceId],
                        },
                        sourceType: {
                          $unshift: [dataType],
                        },
                        aggregation: {
                          $unshift: [aggregation],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // TODO: write a check below. Because at some point we might be adding a number but may not always want to add it to the last position of the source array

      if (dataType === 'decimal' || dataType === 'integer' || dataType === 'binary') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      // introduce step
                      [conditionNumber === null ? 0 : conditionNumber]: {
                        sourceType: {
                          $push: [dataType],
                        },
                        source: {
                          $push: [sourceId],
                        },
                        aggregation: {
                          $push: [aggregation],
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlow.uid]: {
                    // introduce step
                    [conditionNumber === null ? 0 : conditionNumber]: {
                      sourceType: {
                        $push: [dataType],
                      },
                      source: {
                        $push: [sourceId],
                      },
                      aggregation: {
                        $push: [aggregation],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'ADD_CALCULATION_FIELD': {
      const calculationFieldUID = action.payload.calculationFieldUID;
      const calculatiionField = action.payload.calculationField;
      const propKey = action.payload.propKey;

      const calculationSession = state.properties[propKey].currentCalculationSession;

      if (calculationSession && calculationSession.calculationInfo) {
        calculationSession.calculationInfo.fields[calculationFieldUID] = calculatiionField;
      }

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                fields: { [calculationFieldUID]: { $set: calculatiionField } },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'SAVE_CALCULATION': {
      const propKey = action.payload.propKey;
      const uuid = action.payload.uuid;
      const isAggregated = action.payload.isAggregated;
      const datatype = action.payload.datatype;
      const orderedListOfFlows = action.payload?.orderedListOfFlows
      if(orderedListOfFlows.length===0)return state

      let currentCalculationSession: any = state.properties[propKey].currentCalculationSession;
      const savedCalculations = state.savedCalculations;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      // data type for a calculation is going to be according the first source type
      // isAggregation will be there in calculation too, map through all source, if one is aggregated, then the calculation is aggregated

      currentCalculationSession.isAggregated = isAggregated;
      currentCalculationSession.uuid = uuid;
      currentCalculationSession.datatype = datatype;

      // sorting the flows by dependancy order
      const sortedFlows:IFlows = {};
      orderedListOfFlows?.forEach((flowId : string) => {
        sortedFlows[flowId] = currentCalculationSession.calculationInfo.flows[flowId];
      });
      currentCalculationSession.calculationInfo.flows = sortedFlows;
     

      // check if the calculation is already present in the saved calculation list

      const isCalculationGettingUpdated = savedCalculations.find(
        (calculation: any) => calculation.uuid === uuid,
      );

      // check if the condition part is working
      if (isCalculationGettingUpdated) {
        return update(state, {
          savedCalculations: {
            $apply: (calculations: any) => {
              const newCalculations = calculations.filter(
                (calculation: any) => calculation.uuid !== uuid,
              );
              return [...newCalculations, currentCalculationSession];
            },
          },
          fetchRowLevelCalculations: {
            $set: !isAggregated,
          },
        });
      }

      const newState = update(state, {
        savedCalculations: { $push: [currentCalculationSession] },
        fetchRowLevelCalculations: {
          $set: !isAggregated,
        },
      });

      return newState;
    }

    case 'RESET_FETCH_ROW_LEVEL_CALCULATIONS': {
      const newState = update(state, {
        fetchRowLevelCalculations: {
          $set: false,
        },
      });

      return newState;
    }

    case 'SET_FLOW_POSITIONS': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      const x = action.payload.x;
      const y = action.payload.y;
      const propKey = action.payload.propKey;

      const flowPositions = {
        x: x,
        y: y,
      };

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              flowPositions: {
                [calculationFlowUID]: { $set: flowPositions },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'SET_IS_AGGREGATED': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      const isAggregated = action.payload.isAggregated;
      const propKey = action.payload.propKey;
      const step = action.payload.step;
      if (!calculationFlowUID || !propKey || isAggregated === undefined || isAggregated === null)
        return state;
      if (
        !(
          state.properties[propKey].currentCalculationSession &&
          state.properties[propKey].currentCalculationSession.calculationInfo
        )
      )
        return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    [step === null ? 0 : step]: {
                      isAggregation: { $set: isAggregated },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'SET_ACTIVE_FLOW': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      const propKey = action.payload.propKey;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession) return state;
      if (!currentCalculationSession.calculationInfo) return state;

      const activeFlowName =
        calculationFlowUID === null
          ? null
          : currentCalculationSession.calculationInfo.flows[calculationFlowUID][0].flow;

      return {
        ...state,
        properties: {
          ...state.properties,
          [propKey]: {
            ...state.properties[propKey],
            currentCalculationSession: {
              ...state.properties[propKey].currentCalculationSession,
              activeFlow: calculationFlowUID,
              activeCondition: 0,
              activeFlowType:
                activeFlowName === null ? null : getFlowTypeFromFunctionName(activeFlowName),
            },
          },
        },
      };
    }

    case 'DELETE_FLOW': {
      const propKey = action.payload.propKey;
      const calculationFlowUID = action.payload.flowUID;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession?.calculationInfo) return state;
      if (!currentCalculationSession?.flowPositions) return state;

      if (!currentCalculationSession.calculationInfo) return state;

      const allFlowsUids = Object.keys(currentCalculationSession.calculationInfo.flows).filter(
        (flow: any) => flow !== calculationFlowUID,
      );

      const flowName = currentCalculationSession.calculationInfo.flows[calculationFlowUID][0].flow;
      const newActiveFlowType = allFlowsUids[0]
        ? getFlowTypeFromFunctionName(
          currentCalculationSession.calculationInfo.flows[allFlowsUids[0]][0].flow,
        )
        : null;
      if (flowName === 'IfElse') {
        const sourcesToDelete: string[] = [];
        const conditionsToDelete: string[] = [];

        currentCalculationSession.calculationInfo.flows[calculationFlowUID].forEach((flow, id) => {
          flow.sourceType.forEach((sourceType: string, typeIndex: number) => {
            if (sourceType === 'field') {
              sourcesToDelete.push(flow.source[typeIndex]);
            }
          });

          const conditonId = flow.filter;

          if (!currentCalculationSession.calculationInfo?.conditionFilters) return state;

          if (conditonId) {
            for (const leftOp of currentCalculationSession.calculationInfo?.conditionFilters[
              conditonId
            ][0].conditions) {
              if (leftOp.leftOperandType[0] === 'field') {
                sourcesToDelete.push(leftOp.leftOperand[0]);
              }

              if (
                leftOp.rightOperand &&
                leftOp.rightOperandType &&
                leftOp.rightOperandType[0] === 'field'
              ) {
                sourcesToDelete.push(leftOp.rightOperand[0] as string);
              }
            }

            // sourcesToDelete.push(currentCalculationSession.calculationInfo?.conditionFilters[conditonId][0].conditions[0].leftOperand[0])
          }

          // if (
          //   conditonId && currentCalculationSession.calculationInfo?.conditionFilters[conditonId][0]?.conditions &&
          //   currentCalculationSession.calculationInfo.conditionFilters[conditonId][0].conditions.length > 0 &&
          //   currentCalculationSession.calculationInfo.conditionFilters[conditonId][0].conditions[0].leftOperandType[0] === "field"
          // ) {
          //   sourcesToDelete.push(currentCalculationSession.calculationInfo?.conditionFilters[conditonId][0].conditions[0].leftOperand[0]);
          // }

          if (conditonId && currentCalculationSession.calculationInfo?.conditionFilters[conditonId])
            conditionsToDelete.push(conditonId);
        });

        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    $unset: [calculationFlowUID],
                  },
                  fields: {
                    $unset: sourcesToDelete,
                  },
                  conditionFilters: {
                    $unset: conditionsToDelete,
                  },
                },
                flowPositions: {
                  $unset: [calculationFlowUID],
                },
                flowDisplayNames: {
                  $unset: [calculationFlowUID],
                },
                activeFlow: {
                  $set:
                    currentCalculationSession.activeFlow === calculationFlowUID
                      ? allFlowsUids[0]
                      : currentCalculationSession.activeFlow,
                },
                flowParameters: {
                  $unset: [calculationFlowUID],
                },
                activeFlowType: {
                  $set:
                    currentCalculationSession.activeFlow === calculationFlowUID
                      ? newActiveFlowType
                      : currentCalculationSession.activeFlowType,
                },
              },
            },
          },
        });
      }

      const allSourcesRelatedToThisFlow = currentCalculationSession.calculationInfo.flows[
        calculationFlowUID
      ][0].source.filter((src) => typeof src === 'string' && src.length > 0);

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  $unset: [calculationFlowUID],
                },
                fields: {
                  $unset: allSourcesRelatedToThisFlow,
                },
              },
              flowPositions: {
                $unset: [calculationFlowUID],
              },
              flowDisplayNames: {
                $unset: [calculationFlowUID],
              },
              activeFlow: {
                $set:
                  currentCalculationSession.activeFlow === calculationFlowUID
                    ? allFlowsUids[0]
                    : currentCalculationSession.activeFlow,
              },
              flowAggregations: {
                $unset: [calculationFlowUID],
              },
              flowParameters: {
                $unset: [calculationFlowUID],
              },
              activeFlowType: {
                $set:
                  currentCalculationSession.activeFlow === calculationFlowUID
                    ? newActiveFlowType
                    : currentCalculationSession.activeFlowType,
              },
            },
          },
        },
      });

      return newState;
    }

    case 'DELETE_SOURCE': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      const sourceID = action.payload.sourceUID;
      const propKey = action.payload.propKey;
      const sourceIndex = action.payload.sourceIndex;

      // from the source you can get the active condition uid and then delete the condition filter from the condition filters object

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const activeCondition = currentCalculationSession.activeCondition;

      if (activeCondition === null) {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlowUID]: {
                      // introduce step when necessary
                      0: {
                        source: {
                          $apply: (source: any) =>
                            source.filter((id: any, index: number) => index !== sourceIndex),
                        },
                        sourceType: {
                          $apply: (sourceType: any) =>
                            sourceType.filter((id: any, index: number) => index !== sourceIndex),
                        },
                        aggregation: {
                          $apply: (aggregation: any) =>
                            aggregation.filter((id: any, index: number) => index !== sourceIndex),
                        },
                      },
                    },
                  },
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = fields;
                      delete newFields[sourceID];
                      return newFields;
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      } else {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlowUID]: {
                      0: {
                        source: {
                          $apply: (source: any) => {
                            const newSource = source.filter((src: any, id: number) => {
                              return id !== sourceIndex;
                            });
                            return newSource;
                          },
                        },
                        sourceType: {
                          $apply: (sourceType: any) => {
                            const newSourceType = sourceType.filter((id: any, index: number) => {
                              if (index !== sourceIndex) {
                              }
                              return index !== sourceIndex;
                            });
                            return newSourceType;
                          },
                        },
                        aggregation: {
                          $apply: (aggregation: any) => {
                            const newAggregation = aggregation.filter((id: any, index: number) => {
                              return index !== sourceIndex;
                            });
                            return newAggregation;
                          },
                        },
                      },
                    },
                  },
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = { ...fields };
                      delete newFields[sourceID];
                      return newFields;
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      }
    }

    case 'DELETE_SOURCE_WITHOUT_INDEX_SHIFTING': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      const sourceID = action.payload.sourceUID;
      const propKey = action.payload.propKey;
      const sourceIndex = action.payload.sourceIndex;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const activeCondition = currentCalculationSession.activeCondition;

      if (activeCondition === null) {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlowUID]: {
                      0: {
                        source: {
                          $apply: (source: any[]) =>
                            source.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                        sourceType: {
                          $apply: (sourceType: any[]) =>
                            sourceType.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                        aggregation: {
                          $apply: (aggregation: any[]) =>
                            aggregation.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                      },
                    },
                  },
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = { ...fields };
                      delete newFields[sourceID];
                      return newFields;
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      } else {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlowUID]: {
                      0: {
                        source: {
                          $apply: (source: any[]) =>
                            source.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                        sourceType: {
                          $apply: (sourceType: any[]) =>
                            sourceType.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                        aggregation: {
                          $apply: (aggregation: any[]) =>
                            aggregation.map((item, index) => (index === sourceIndex ? null : item)),
                        },
                      },
                    },
                  },
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = { ...fields };
                      delete newFields[sourceID];
                      return newFields;
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      }
    }

    /**
     * Next case is  added as a part of the refactoring process
     * the state structure has been changed  for condition filter  filterIdx is needed to delete condition filter
     *
     * do not use this reducer outside ifelse
     */
    case 'DELETE_SOURCE_V2': {
      const calculationFlowUID = action.payload.calculationFlowUID;
      // const sourceID = action.payload.sourceUID;
      const propKey = action.payload.propKey;
      // const sourceIndex = action.payload.sourceIndex;
      const filterIdx = action.payload.filterIdx;
      // from the source you can get the active condition uid and then delete the condition filter from the condition filters object

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const activeCondition = currentCalculationSession.activeCondition;
      if (activeCondition === null) return state;
      const activeConditionUid =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][activeCondition].filter;
      const fieldId =
        currentCalculationSession?.calculationInfo?.conditionFilters?.[`${activeConditionUid}`]?.[0]
          ?.conditions?.[filterIdx]?.leftOperand?.[0] ?? '';
      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                fields: {
                  $apply: (fields: any) => {
                    const newFields = { ...fields };
                    delete newFields[fieldId];
                    return newFields;
                  },
                },
                conditionFilters: {
                  [`${activeConditionUid}`]: {
                    0: {
                      lastDroppedEvaluationUid: { $set: null },
                      conditions: {
                        $splice: [[filterIdx, 1]],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }
    /**
     * for v3 threr
     */
    case 'DELETE_SOURCE_V3': {
      return state;
    }
    case 'MOVE_SOURCE_CARD': {
      let { dragIndex, hoverIndex, propKey, calculationFlowUID, step } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (step === null) step = 0;
      const sourceBackup =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][step].source[dragIndex];
      const sourceTypeBackup =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][step].sourceType[
        dragIndex
        ];
      const aggregationBackup =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][step].aggregation[
        dragIndex
        ];

      if (!sourceTypeBackup || !aggregationBackup) return state;
      if (sourceBackup === undefined || sourceBackup === null) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    [step]: {
                      source: {
                        $splice: [
                          [dragIndex, 1],
                          [hoverIndex, 0, sourceBackup],
                        ],
                      },
                      sourceType: {
                        $splice: [
                          [dragIndex, 1],
                          [hoverIndex, 0, sourceTypeBackup],
                        ],
                      },
                      aggregation: {
                        $splice: [
                          [dragIndex, 1],
                          [hoverIndex, 0, aggregationBackup],
                        ],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_SOURCE_AGGREGATION': {
      const { propKey, calculationFlowUID, step, sourceIndex, newAggregation } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    [step]: {
                      aggregation: {
                        $splice: [[sourceIndex, 1, newAggregation]],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_FLOW_POSITIONS': {
      const { propKey, calculationFlowUID, x, y } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              flowPositions: {
                [calculationFlowUID]: {
                  $set: {
                    x,
                    y,
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }
    case 'UPDATE_FLOW_CONDITION': {
      const { propKey, calculationFlowUID, conditionIndex, condition } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const conditionFilterID =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][conditionIndex].filter;
      let newConditions =
        currentCalculationSession.calculationInfo.conditionFilters[conditionFilterID!][0]
          .conditions;
      let source =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][conditionIndex].source;
      let sourceType =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][conditionIndex]
          .sourceType;
      let fields = currentCalculationSession.calculationInfo.fields;
      if (condition === 'Else') {
        const fildsToBeDeleted = source;
        fields = Object.keys(fields).reduce((acc: any, field: any) => {
          if (fildsToBeDeleted.includes(field)) {
            return acc;
          }
          return { ...acc, [field]: fields[field] };
        }, {});
        newConditions = [];
        source = [];
        sourceType = [];
      }
      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    [conditionIndex]: {
                      condition: { $set: condition },
                      source: { $set: source },
                      sourceType: { $set: sourceType },
                    },
                  },
                },
                fields: { $set: fields },
                conditionFilters: {
                  [conditionFilterID!]: {
                    0: {
                      conditions: {
                        $set: newConditions,
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
      return newState;
    }

    case 'ADD_CONDITION_FILTER': {
      const { propKey, conditionFilter } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const calculationFlowUID = currentCalculationSession.activeFlow;
      const activeCondition = currentCalculationSession.activeCondition;

      if (calculationFlowUID === null || activeCondition === null) return state;

      const activeConditionUID =
        currentCalculationSession.calculationInfo.flows[calculationFlowUID][activeCondition].filter;

      if (!activeConditionUID) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [activeConditionUID]: {
                    0: {
                      lastDroppedEvaluationUid: {
                        $set: conditionFilter.conditionUID,
                      },
                      conditions: {
                        $push: [conditionFilter],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }
    case 'DELETE_CONDITION_FROM_IF_ELSE': {
      const { propKey, flowId, conditionUid } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const flow = currentCalculationSession.calculationInfo.flows[flowId];
      const conditionIndex = flow.findIndex((flow: any) => flow.flowId === conditionUid);
      if (conditionIndex === -1) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    $splice: [[conditionIndex, 1]],
                  },
                },
              },
              activeCondition: {
                $set: 0,
              },
            },
          },
        },
      });
    }
    case 'UPDATE_SEARCH_CONDITION_FILTER': {
      const {
        propKey,
        conditionFilterUid, // example: flt0
        sourceUid, // example: f1
        shouldExclude, // example: false
      } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const activeConditionIndex = currentCalculationSession.activeCondition;
      if (activeConditionIndex === null) return state;

      const flowUid = currentCalculationSession.activeFlow;

      if (!flowUid) return state;

      const sourceIndex = currentCalculationSession.calculationInfo.flows[flowUid][
        activeConditionIndex
      ].source.findIndex((id: string) => id === sourceUid);

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterUid]: {
                    [sourceIndex]: {
                      conditions: {
                        0: {
                          shouldExclude: { $set: shouldExclude },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }
    case 'DELETE_CONDITION_FILTER': {
      const { propKey, conditionFilterUid } = action.payload;
      if (conditionFilterUid === '') return state;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (
        !currentCalculationSession ||
        !currentCalculationSession.calculationInfo ||
        !currentCalculationSession.calculationInfo.conditionFilters
      )
        return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  $unset: [conditionFilterUid],
                },
              },
            },
          },
        },
      });
    }
    case 'UPDATE_CONDITION_FILTER': {
      const {
        propKey,
        conditionFilterUid,
        conditionFilter, // example: f1
        filterIdx,
      } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const activeConditionIndex = currentCalculationSession.activeCondition;
      if (activeConditionIndex === null) return state;

      const flowUid = currentCalculationSession.activeFlow;

      if (!flowUid) return state;
      const leftOprnd =
        currentCalculationSession.calculationInfo.conditionFilters[conditionFilterUid][0]
          .conditions[filterIdx].leftOperand;
      const conditionUID =
        currentCalculationSession.calculationInfo.conditionFilters[conditionFilterUid][0]
          .conditions[filterIdx].conditionUID;
      const filterType =
        currentCalculationSession.calculationInfo.conditionFilters[conditionFilterUid][0]
          .conditions[filterIdx].filterType;

      const leftOprndType =
        currentCalculationSession.calculationInfo.conditionFilters[conditionFilterUid][0]
          .conditions[filterIdx].leftOperandType;
      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterUid]: {
                    0: {
                      conditions: {
                        [filterIdx]: {
                          $merge: {
                            conditionUID: conditionUID,
                            filterType: filterType,
                            leftOperand: leftOprnd,
                            leftOperandType: leftOprndType,
                            isValid: conditionFilter.isValid ?? false,
                            ...conditionFilter,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
      return newState;
    }
    case 'UPDATE_FILTER_OPERATOR': {
      const { propKey, conditionFilterUid, operandIdx, operator } = action.payload;

      if (!conditionFilterUid || operandIdx === null || operandIdx === undefined) return state;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters) return state;
      const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;
      if (!conditionFilters[conditionFilterUid]) return state;
      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterUid]: {
                    0: {
                      conditions: {
                        [operandIdx]: {
                          operator: { $set: operator },
                          rightOperand: { $set: [] },
                          rightOperandType: { $set: [] },
                          isValid: { $set: false },
                          parameterId: { $set: [] },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_TIMEGRAIN': {
      const { propKey, conditionFilterUid, filterIdx, timeGrain } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const activeConditionIndex = currentCalculationSession.activeCondition;
      if (activeConditionIndex === null) return state;

      const flowUid = currentCalculationSession.activeFlow;

      if (!flowUid) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterUid]: {
                    0: {
                      conditions: {
                        [filterIdx]: {
                          timeGrain: { $set: timeGrain },
                          rightOperand: { $set: [] },
                          rightOperandType: { $set: [] },
                          isValid: { $set: false },
                          parameterId: { $set: [] },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'TOGGLE_IS_TILL_DATE': {
      const { propKey, conditionFilterUid, sourceIndex } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const activeConditionIndex = currentCalculationSession.activeCondition;
      if (activeConditionIndex === null) return state;

      const flowUid = currentCalculationSession.activeFlow;

      if (!flowUid) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterUid]: {
                    [sourceIndex]: {
                      conditions: {
                        0: {
                          isTillDate: {
                            $set: !currentCalculationSession.calculationInfo.conditionFilters[
                              conditionFilterUid
                            ][sourceIndex].conditions[0].isTillDate,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_DECIMAL_SOURCE_VALUE': {
      const { propKey, sourceIndex, value } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const activeConditionIndex = currentCalculationSession.activeCondition;
      const flowUid = currentCalculationSession.activeFlow;
      if (!flowUid) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowUid]: {
                    [activeConditionIndex === null ? 0 : activeConditionIndex]: {
                      source: {
                        [sourceIndex]: { $set: Number(value) },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
      return newState;
    }

    case 'UPDATE_SOURCE': {
      let { propKey, sourceIndex, newSourceValue } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const activeFlow = currentCalculationSession.activeFlow;
      if (!activeFlow) return state;
      const activeFlowName = currentCalculationSession.calculationInfo.flows[activeFlow][0].flow;
      const totalSourceLength =
        currentCalculationSession.calculationInfo.flows[activeFlow][0].source.length;

      if (activeFlowName === 'replace') {
        totalSourceLength === 3 ? (sourceIndex = sourceIndex + 1) : (sourceIndex = sourceIndex);
      } else if (activeFlowName === 'split') {
        totalSourceLength === 4 ? (sourceIndex = sourceIndex + 1) : (sourceIndex = sourceIndex);
      } else if (activeFlowName === 'substringleft' || activeFlowName === 'substringright') {
        totalSourceLength === 3 ? (sourceIndex = sourceIndex + 1) : (sourceIndex = sourceIndex);
      }

      const sourceType =
        currentCalculationSession.calculationInfo.flows[activeFlow][0].sourceType[sourceIndex];

      let newState = state;

      if (
        sourceType === 'text' &&
        (activeFlowName === 'substringleft' || activeFlowName === 'substringright')
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (
        sourceType === 'decimal' &&
        (activeFlowName === 'substringleft' || activeFlowName === 'substringright')
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (sourceType === 'text' && activeFlowName === 'replace') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      [0]: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (sourceType === 'text' && activeFlowName === 'split') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      [0]: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (sourceType === 'decimal' && activeFlowName === 'split') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      [0]: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (sourceType === 'text' && activeFlowName === 'split') {
        const possibleDirections = ['left', 'right'];

        const isDirection = possibleDirections.includes(newSourceValue);
        let left: number;

        if (!isDirection) {
          return update(state, {
            properties: {
              [propKey]: {
                currentCalculationSession: {
                  calculationInfo: {
                    flows: {
                      [activeFlow]: {
                        [0]: {
                          source: {
                            [sourceIndex]: {
                              $set: newSourceValue,
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          });
        }
      }

      if (
        sourceType === 'text' &&
        activeFlowName === 'concat' &&
        sourceIndex >= 0 &&
        sourceIndex <= 5
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      [0]: {
                        source: {
                          [sourceIndex]: {
                            $set: newSourceValue,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      return newState;
    }

    case 'UPDATE_FLOW_DISPLAY_NAME': {
      const { propKey, flowUID, displayName } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              flowDisplayNames: {
                [flowUID]: { $set: displayName },
              },
            },
          },
        },
      });

      return newState;
    }
    case 'SORT_CONDITION_IN_IF_ELSE': {
      const { propKey, flowId, dragConditionUid, dropConditionUid } = action.payload;
      const dropIndex = findIndex(propKey, flowId, dropConditionUid);
      const draggedItem = findInfo(propKey, flowId, dragConditionUid);
      if (draggedItem === null || dropIndex === -1) return state;
      const _currentFlowOrder =
        state.properties[propKey]?.currentCalculationSession?.calculationInfo?.flows[flowId];
      if (!_currentFlowOrder) return state;

      const [item] = _currentFlowOrder.splice(draggedItem.conditionIndex, 1);
      _currentFlowOrder.splice(dropIndex, 0, item);
      const currentFlowOrder = [..._currentFlowOrder];

      if (!currentFlowOrder[dropIndex] || !currentFlowOrder[draggedItem.conditionIndex])
        return state;

      _currentFlowOrder[0].condition = 'If';
      for (let i = 1; i < _currentFlowOrder.length; i++) {
        const prevType = i > 0 ? _currentFlowOrder[i - 1].condition : null;
        const nextType =
          i < _currentFlowOrder.length - 1 ? _currentFlowOrder[i + 1]?.condition : null;
        if (_currentFlowOrder[i].condition === 'If') {
          if (prevType === 'ElseIf' && nextType === 'ElseIf') {
            _currentFlowOrder[i].condition = 'ElseIf';
          }
        } else if (_currentFlowOrder[i].condition === 'ElseIf') {
          if (prevType === 'If' && nextType === 'If') {
            _currentFlowOrder[i].condition = 'If';
          }
        } else {
          if (i !== _currentFlowOrder.length - 1) {
            if (prevType === 'If') {
              _currentFlowOrder[i].condition = 'If';
            } else _currentFlowOrder[i].condition = 'ElseIf';
          }
        }
      }
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    $set: currentFlowOrder,
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'REVERT': {
      const { propKey, flowId, conditionList } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const flow = currentCalculationSession.calculationInfo.flows[flowId];
      if (!flow) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    $set: conditionList,
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'CORRECT_CONDITION_TYPE_FLOW': {
      const { propKey, flowId } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const flow = currentCalculationSession.calculationInfo.flows[flowId];
      if (!flow) return state;
      let prevConditionType = 'If';
      const newFlow = flow.map((conditionInfo: ICalculationStep, index: number) => {
        if (index === 0) {
          return {
            ...conditionInfo,
            condition: 'If',
          };
        }
        if (conditionInfo.condition !== prevConditionType) {
          if (index + 1 >= flow.length) {
            return {
              ...conditionInfo,
              condition: conditionInfo.condition === 'If' ? 'Else' : conditionInfo.condition,
            };
          }
          const nextConditionType = flow[index + 1].condition;
          if (nextConditionType !== conditionInfo.condition) {
            prevConditionType = nextConditionType!;
            return {
              ...conditionInfo,
              condition: nextConditionType,
            };
          }
        }
        prevConditionType = conditionInfo.condition!;
        return conditionInfo;
      });
      prevConditionType = 'If';
      const _newFlow = flow.map((conditionInfo: ICalculationStep, index: number) => {
        if (index === 0) {
          return {
            ...conditionInfo,
            condition: 'If',
          };
        }
        if (conditionInfo.condition === 'If' && prevConditionType !== 'If') {
          return {
            ...conditionInfo,
            condition: prevConditionType,
          };
        } else if (conditionInfo.condition === 'ElseIf' && prevConditionType !== 'ElseIf') {
          return {
            ...conditionInfo,
            condition: prevConditionType,
          };
        }
      });
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    $set: newFlow,
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'UPDATE_CALCULATION_NAME': {
      const { propKey, calculationName } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              name: { $set: calculationName },
              // calculationInfo:
            },
          },
        },
      });
      return newState;
    }
    case 'ADD_RESULT_FOR_IF_ELSE': {
      const { propKey, flowId, conditionIndex, sourceId, sourceType } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.flows[flowId][conditionIndex]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [conditionIndex]: {
                      source: { $set: [sourceId] },
                      sourceType: { $set: [sourceType] },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'SET_RESULT_TYPE_FOR_IF_ELSE': {
      const { propKey, flowId, resultType } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                selectedResultType: {
                  [flowId]: { $set: resultType },
                },
              },
            },
          },
        },
      });
    }
    case 'RESET_RESULT_FOR_IF_ELSE': {
      const { propKey, flowId, conditionIndex } = action.payload;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.flows[flowId][conditionIndex]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [conditionIndex]: {
                      source: { $set: [] },
                      sourceType: { $set: [] },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'DELETE_RESULT_FOR_IF_ELSE': {
      const { propKey, flowId, conditionIndex } = action.payload;
      if (propKey == null || flowId == null || conditionIndex == null) return state;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (
        !(
          currentCalculationSession &&
          currentCalculationSession.calculationInfo &&
          currentCalculationSession.activeFlow
        )
      )
        return state;

      const currentCondition =
        currentCalculationSession.calculationInfo.flows[flowId][conditionIndex];
      const onlyConditionWithResultField =
        currentCalculationSession.calculationInfo.flows[
          currentCalculationSession.activeFlow
        ].filter(
          (condition) =>
            condition.source.length === 1 && condition.flowId !== currentCondition.flowId,
        ).length === 0;

      const sourceToRemove = currentCondition.source[0];

      // Create a new state with immutability-helper
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [conditionIndex]: {
                      source: { $set: [] },
                      sourceType: { $set: [] },
                    },
                  },
                },
                fields: {
                  $unset: [sourceToRemove],
                },
                selectedResultType: {
                  [currentCalculationSession.activeFlow]: {
                    $set: onlyConditionWithResultField
                      ? null
                      : currentCalculationSession.calculationInfo.selectedResultType[
                      currentCalculationSession.activeFlow
                      ],
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'EDIT_SAVED_CALCULATION': {
      // obviously first set the calculation to be current calculation session, then delete the old one
      const { calculationFieldName, propKey } = action.payload;

      // from this field name we can get the whole saved calculation object
      const calculationToBeEdited = state.savedCalculations.find((calc: any) => {
        return (
          calc.calculationInfo.calculatedFieldName.toLowerCase().replace(/[^a-z0-9]/g, '') ===
          calculationFieldName.toLowerCase().replace(/[^a-z0-9]/g, '')
        );
      });

      if (!calculationToBeEdited) return state;

      const newState = update(state, {
        properties: {
          $merge: {
            [propKey]: {
              currentCalculationSession: calculationToBeEdited,
            },
          },
        },
      });

      return newState;
    }

    case 'DELETE_SAVED_CALCULATION': {
      const { calculationFieldName, propKey } = action.payload;

      const indexOfToBeDeleted = state.savedCalculations.findIndex((calc: any) => {
        return calc.calculationInfo.calculatedFieldName === calculationFieldName;
      });

      const newState = update(state, {
        savedCalculations: {
          $splice: [[indexOfToBeDeleted, 1]],
        },
      });

      return newState;
    }

    case 'SET_SOURCE': {
      let { propKey, flowId, subFlowId, source, sourceType, sourceIndex } = action.payload;
      if (!subFlowId) subFlowId = 0;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.flows[flowId][subFlowId]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      source: { $splice: [[sourceIndex, 1, source]] },
                      sourceType: { $splice: [[sourceIndex, 1, sourceType]] },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'RESET_SOURCE': {
      let { propKey, flowId, subFlowId } = action.payload;
      if (!subFlowId) subFlowId = 0;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.flows[flowId][subFlowId]) return state;
      const fieldsToBeDeleted = currentCalculationSession.calculationInfo.flows[flowId][
        subFlowId
      ].sourceType
        .map((sourceType: string, idx: number) => (sourceType === 'field' ? idx : -1))
        .filter((idx: number) => idx !== -1)
        .map(
          (idx: number) =>
            currentCalculationSession.calculationInfo?.flows[flowId][subFlowId].source[idx],
        );
      const fields = currentCalculationSession.calculationInfo.fields;
      const updatedFields = {
        ...currentCalculationSession.calculationInfo.fields,
      };
      fieldsToBeDeleted.forEach((field) => {
        delete updatedFields[field];
      });
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      source: { $set: [] },
                      sourceType: { $set: [] },
                    },
                  },
                },
                fields: { $set: updatedFields },
              },
            },
          },
        },
      });
    }
    case 'ADD_SOURCE_WITH_RESTRICTION': {
      let { propKey, flowId, subFlowId, source, sourceType, sourceIndex } = action.payload;
      if (!subFlowId) subFlowId = 0;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const flow = currentCalculationSession.calculationInfo.flows[flowId]?.[subFlowId];
      if (!flow) return state;

      // Ensure arrays are properly initialized
      const sources = flow.source || [];
      const sourceTypes = flow.sourceType || [];

      // Insert at the specified index or append at the end
      const newSources = [...sources];
      const newSourceTypes = [...sourceTypes];

      if (sourceIndex !== undefined && sourceIndex >= 0 && sourceIndex < newSources.length) {
        newSources.splice(sourceIndex, 0, source); // Insert at the specific index
        newSourceTypes.splice(sourceIndex, 0, sourceType);
      } else {
        newSources.push(source); // Append at the end
        newSourceTypes.push(sourceType);
      }

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      source: { $set: newSources },
                      sourceType: { $set: newSourceTypes },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'ADD_SOURCE_WITHOUT_RESTRICTION': {
      let { propKey, flowId, subFlowId, source, sourceType, sourceIndex } = action.payload;
      if (!subFlowId) subFlowId = 0;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const flow = currentCalculationSession.calculationInfo.flows[flowId]?.[subFlowId];
      if (!flow) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      source: {
                        [sourceIndex]: { $set: source },
                      },
                      sourceType: {
                        [sourceIndex]: { $set: sourceType },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'DELETE_SOURCE_V4': {
      let { propKey, flowId, subFlowId, sourceIndex } = action.payload;
      if (!subFlowId) subFlowId = 0;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const flow = currentCalculationSession.calculationInfo.flows[flowId]?.[subFlowId];
      if (!flow) return state;

      // Ensure arrays are properly initialized
      const sources = flow.source || [];
      const sourceTypes = flow.sourceType || [];

      // Insert at the specified index or append at the end
      const newSources = [...sources];
      const newSourceTypes = [...sourceTypes];
      newSources.splice(sourceIndex, 1);
      newSourceTypes.splice(sourceIndex, 1);

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      source: { $set: newSources },
                      sourceType: { $set: newSourceTypes },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'ADD_TABLE_ID_TO_CURRENT_CALCULATION_SESSION': {
      const { tableId, propKey } = action.payload;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              tableId: {
                $set: tableId,
              },
            },
          },
        },
      });
    }

    case 'INSERT_FLOW_SOURCE': {
      // TODO: add support of If else

      const { propKey, flowUidOfDestinationFlow, flowUidOfValueFlow } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const flow = currentCalculationSession.calculationInfo.flows[flowUidOfDestinationFlow];
      if (!flow) return state;

      if (['log', 'split', 'substringleft', 'substringright', 'replace'].includes(flow[0].flow)) {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [flowUidOfDestinationFlow]: {
                      0: {
                        source: { $unshift: [flowUidOfValueFlow] },
                        sourceType: { $unshift: ['flow'] },
                        aggregation: { $unshift: ['sum'] },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      }

      if (flow[0].flow !== 'IfElse') {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [flowUidOfDestinationFlow]: {
                      0: {
                        source: { $push: [flowUidOfValueFlow] },
                        sourceType: { $push: ['flow'] },
                        aggregation: { $push: ['sum'] },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      } else return state;
    }

    case 'SET_AGGREGATION_FOR_FLOW': {
      const { calculationFlowUID, isAggregation, propKey } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.flows[calculationFlowUID]) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              flowAggregations: {
                [calculationFlowUID]: { $set: isAggregation },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'FIELD_FLOW_RIGHT_OPERAND': {
      const {
        conditionFilterId,
        leftOperandIndex,
        rightOperandType,
        rightOperand,
        propKey,
        fieldId,
      } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId])
        return state;

      if (rightOperandType === 'field') {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = {
                        ...fields,
                        [fieldId]: {
                          displayName: rightOperand.displayname,
                          dataType: rightOperand.dataType,
                          fieldName: rightOperand.fieldname,
                          tableId: rightOperand.tableId,
                        },
                      };
                      return newFields;
                    },
                  },
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: { $set: [fieldId] },
                            rightOperandType: { $set: [rightOperandType] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
        return newState;
      } else if (rightOperandType === 'flow') {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: { $set: [fieldId] },
                            rightOperandType: { $set: [rightOperandType] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
        return newState;
      }

      return state;
    }

    case 'FIELD_FLOW_RIGHT_OPERAND_BETWEEN': {
      const {
        conditionFilterId,
        leftOperandIndex,
        rightOperandType,
        rightOperand,
        propKey,
        fieldId,
        dropzoneFlag,
      } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId])
        return state;

      // Determine the target index based on dropzoneFlag
      const targetIndex = dropzoneFlag === 'first' ? 0 : 1;

      if (rightOperandType === 'field') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  fields: {
                    $apply: (fields: any) => ({
                      ...fields,
                      [fieldId]: {
                        displayName: rightOperand.displayname,
                        dataType: rightOperand.dataType,
                        fieldName: rightOperand.fieldname,
                        tableId: rightOperand.tableId,
                      },
                    }),
                  },
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: {
                              $apply: (operands: any = []) => {
                                operands[targetIndex] = fieldId;
                                return operands;
                              },
                            },
                            rightOperandType: {
                              $apply: (types: any = []) => {
                                types[targetIndex] = rightOperandType;
                                return types;
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      } else if (rightOperandType === 'flow') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: {
                              $apply: (operands: any = []) => {
                                operands[targetIndex] = fieldId;
                                return operands;
                              },
                            },
                            rightOperandType: {
                              $apply: (types: any = []) => {
                                types[targetIndex] = rightOperandType;
                                return types;
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      return state;
    }

    case 'SET_SHOW_FIRST_FOR_CONDITION': {
      const { propKey, conditionFilterId, filterIdx, value } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId])
        return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [filterIdx]: {
                          showFirst: { $set: value },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'SET_SHOW_SECOND_FOR_CONDITION': {
      const { propKey, conditionFilterId, filterIdx, value } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId])
        return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [filterIdx]: {
                          showSecond: { $set: value },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'DELETE_FIELD_FLOW_RIGHT_OPERAND': {
      const { conditionFilterId, leftOperandIndex, rightOperandType, propKey, fieldId } =
        action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters[conditionFilterId])
        return state;

      if (rightOperandType === 'field') {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = { ...fields };
                      delete newFields[fieldId];
                      return newFields;
                    },
                  },
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: { $set: [] },
                            rightOperandType: { $set: [] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      } else if (rightOperandType === 'flow') {
        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: { $set: [] },
                            rightOperandType: { $set: [] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      }

      return state;
    }

    case 'DELETE_FIELD_FLOW_RIGHT_OPERAND_BETWEEN': {
      const {
        conditionFilterId,
        leftOperandIndex,
        rightOperandType,
        propKey,
        fieldId,
        dropzoneFlag,
      } = action.payload;

      const currentCalculationSession = state.properties?.[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters?.[conditionFilterId])
        return state;

      if (rightOperandType === 'field' || rightOperandType === 'flow') {
        const targetIndex = dropzoneFlag === 'first' ? 0 : 1;

        const newState = update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  fields: {
                    $apply: (fields: any) => {
                      const newFields = { ...fields };
                      if (newFields[fieldId]) {
                        delete newFields[fieldId];
                      }
                      return newFields;
                    },
                  },
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [leftOperandIndex]: {
                            rightOperand: {
                              [targetIndex]: { $set: '' }, // Only clear the specific index
                            },
                            rightOperandType: {
                              [targetIndex]: { $set: '' }, // Only clear the specific index
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });

        return newState;
      }

      return state;
    }

    case 'DELETE_RIGHT_OPERAND_FOR_STATIC_VALUE': {
      const { conditionFilterId, leftOperandIndex, propKey } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [leftOperandIndex]: {
                          rightOperand: { $set: [] },
                          rightOperandType: { $set: [] },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'CHANGE_IS_VALID_STATE': {
      const { propKey, filterId, conditionIndex, isValid } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [filterId]: {
                    [0]: {
                      conditions: {
                        [conditionIndex]: {
                          isValid: { $set: isValid },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }
    case 'DELETE_RIGHT_OPERAND_FOR_STATIC_VALUE_BETWEEN': {
      const { conditionFilterId, leftOperandIndex, propKey, targetIndex } = action.payload;

      const currentCalculationSession = state.properties?.[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters?.[conditionFilterId])
        return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [leftOperandIndex]: {
                          rightOperand: {
                            [targetIndex]: { $set: '' }, // Only clear the specific index
                          },
                          rightOperandType: {
                            [targetIndex]: { $set: '' }, // Only clear the specific index
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_CALCULATED_FIELD_NAME': {
      const { propKey, calculationFieldName } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                calculatedFieldName: { $set: calculationFieldName },
              },
            },
          },
        },
      });
    }

    case 'UPDATE_DISPLAY_FIELD_NAME': {
      const { propKey, displayFieldName } = action.payload;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                displayFieldName: { $set: displayFieldName },
              },
            },
          },
        },
      });
    }

    case 'SET_FILTER_TYPE': {
      const { propKey, filterType, conditionFilterId, operandIndex } = action.payload;
      if (!propKey || !filterType || !conditionFilterId || operandIndex == null) return state;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters) return state;
      const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;
      if (!conditionFilters[conditionFilterId]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [operandIndex]: {
                          filterType: { $set: filterType },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'SET_SHOULD_EXCLUDE': {
      const { propKey, conditionFilterId, operandIndex, value = false } = action.payload;
      if (!propKey || !conditionFilterId || operandIndex == null) return state;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters) return state;
      const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;
      if (!conditionFilters[conditionFilterId]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [operandIndex]: {
                          shouldExclude: { $set: value },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'SET_RIGHT_OPERAND': {
      const {
        propKey,
        conditionFilterId,
        operandIndex,
        rightOperand = [],
        rightOperandType = [],
      } = action.payload;
      if (!propKey || !conditionFilterId || operandIndex == null) return state;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters) return state;
      const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;
      if (!conditionFilters[conditionFilterId]) return state;
      const condition = conditionFilters[conditionFilterId][0].conditions[operandIndex];
      if (!condition) return state;
      const oldRightOperandType = condition.rightOperandType || [];

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [operandIndex]: {
                          rightOperand: { $set: rightOperand },
                          rightOperandType: { $set: rightOperandType },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'SET_RELATIVE_CONDITION': {
      const { propKey, conditionFilterId, operandIndex, relativeCondition } = action.payload;
      if (!propKey || !conditionFilterId || operandIndex == null) return state;
      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      if (!currentCalculationSession.calculationInfo.conditionFilters) return state;
      const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;
      if (!conditionFilters[conditionFilterId]) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [operandIndex]: {
                          relativeCondition: { $set: relativeCondition },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'ADD_FIELD': {
      const { propKey, fieldId, fieldName, dataType, displayName, tableId } = action.payload;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                fields: {
                  [fieldId]: {
                    $set: {
                      fieldName,
                      dataType,
                      displayName,
                      tableId,
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'DELETE_FIELD': {
      const { propKey, fieldId } = action.payload;
      if(!propKey || !fieldId) return state;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;
      const updatedFields = {
        ...currentCalculationSession.calculationInfo.fields,
      };
      delete updatedFields[fieldId];
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                fields: { $set: updatedFields },
              },
            },
          },
        },
      });
    }

    case 'UPDATE_FILTER_OVERRIDE': {
      const { propKey, filterOverrideInfo } = action.payload;
      if (!propKey || !filterOverrideInfo) return state;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) {
        return state;
      }

      const activeFlow = currentCalculationSession.activeFlow;

      const hasOverride =
        !!currentCalculationSession.calculationInfo.flows[activeFlow]?.[0]?.flowOverride;
      const currentValue = currentCalculationSession.calculationInfo.flows[activeFlow]?.[0] || {};
      const overrideCount = currentCalculationSession.calculationInfo.overrideCount;
      if (!hasOverride) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          $set: {
                            filter: filterOverrideInfo,
                            dimension: {
                              type: 'honorAll',
                              dimensions: [] as IFlowOverrideField[],
                            },
                            windowFunction: currentValue.flowOverride?.windowFunction ?? null,
                          },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $set:
                      filterOverrideInfo.type !== 'honorAll' ? overrideCount + 1 : overrideCount,
                  },
                },
              },
            },
          },
        });
      } else {
        const overrides =
          currentCalculationSession.calculationInfo.flows[activeFlow]?.[0]?.flowOverride;
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          filter: { $set: filterOverrideInfo },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $apply: (currOverrideCount: number) => {
                      const alreadyCounted =
                        overrides?.dimension?.type !== 'honorAll' ||
                        overrides?.filter?.type !== 'honorAll';
                      const canAdd =
                        overrides?.dimension?.type !== 'honorAll' ||
                        filterOverrideInfo.type !== 'honorAll';
                      if (!alreadyCounted && canAdd) return currOverrideCount + 1;
                      if (alreadyCounted && !canAdd) return currOverrideCount - 1;
                      return currOverrideCount;
                    },
                  },
                },
              },
            },
          },
        });
      }
    }
    case 'UPDATE_DIMENSION_OVERRIDE': {
      const { propKey, dimensionOverrideInfo } = action.payload;
      if (!propKey || !dimensionOverrideInfo) return state;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) {
        return state;
      }

      const activeFlow = currentCalculationSession.activeFlow;

      const hasOverride =
        !!currentCalculationSession.calculationInfo.flows[activeFlow]?.[0]?.flowOverride;
      const overrideCount = currentCalculationSession.calculationInfo.overrideCount;
      const currentValue = currentCalculationSession.calculationInfo.flows[activeFlow]?.[0] || {};
      if (!hasOverride) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          $set: {
                            filter: {
                              type: 'honorAll',
                              isFilterGroup: false,
                              filterGroups: [],
                              filters: [] as IFlowOverrideField[],
                              filterPanel: {
                                panelName: 'overrideFilter',
                                shouldAllConditionsMatch: true,
                                filters: [],
                              },
                            },
                            dimension: dimensionOverrideInfo,
                            windowFunction: currentValue.flowOverride?.windowFunction ?? null,
                          },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $set:
                      dimensionOverrideInfo.type !== 'honorAll' ? overrideCount + 1 : overrideCount,
                  },
                },
              },
            },
          },
        });
      } else {
        const overrides =
          currentCalculationSession.calculationInfo.flows[activeFlow]?.[0]?.flowOverride;
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [activeFlow]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          dimension: { $set: dimensionOverrideInfo },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $apply: (currOverrideCount: number) => {
                      const alreadyCounted =
                        overrides?.dimension?.type !== 'honorAll' ||
                        overrides?.filter?.type !== 'honorAll';
                      const canAdd =
                        overrides?.filter?.type !== 'honorAll' ||
                        dimensionOverrideInfo.type !== 'honorAll';
                      if (!alreadyCounted && canAdd) return currOverrideCount + 1;
                      if (alreadyCounted && !canAdd) return currOverrideCount - 1;
                      return currOverrideCount;
                    },
                  },
                },
              },
            },
          },
        });
      }
    }

    case 'UPDATE_FIELD_OF_DIMENSION_OVERRIDE': {
      const { field, propKey, operation } = action.payload;
      if (!propKey || !field) return state;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) {
        return state;
      }

      const activeFlow = currentCalculationSession.activeFlow;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      overrided: { $set: true },
                      flowOverride: {
                        dimension: {
                          dimensions: {
                            $apply: (dimensions: IFlowOverrideField[]) => {
                              if (operation === 'add') {
                                return [...dimensions, field];
                              } else if (operation === 'rmv') {
                                return dimensions.filter((dim) => dim.uid !== field.uid);
                              } else if (operation === 'update') {
                                return dimensions.map((dim) =>
                                  dim.uid === field.uid
                                    ? ({ ...field } as IFlowOverrideField)
                                    : dim,
                                );
                              } else return dimensions;
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'SET_AGGREGATION_VALUE': {
      const { propKey, flowId, subFlowId, aggregation, resultDataType } = action.payload;
      if (!propKey || !flowId || !aggregation) return state;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession) return state;
      if (!currentCalculationSession.calculationInfo.flows[flowId]) return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [flowId]: {
                    [subFlowId]: {
                      aggregation: { $set: [aggregation] },
                      isAggregation: { $set: true },
                      resultantDatatype: {
                        $apply: (current: string | null) => {
                          return resultDataType ?? current;
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'SET_IS_FILTER_GROUP': {
      const { propKey, value } = action.payload;
      if (!propKey || typeof value !== 'boolean') return state;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) {
        return state;
      }

      const activeFlow = currentCalculationSession.activeFlow;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      overrided: { $set: true },
                      flowOverride: {
                        filter: {
                          isFilterGroup: { $set: value },
                          filterGroups: {
                            $apply: (curr: string[]) => {
                              if (!value) return [];
                              return curr;
                            },
                          },
                          filters: {
                            $apply: (curr: IFlowOverrideField[]) => {
                              if (value) return [];
                              return curr;
                            },
                          },
                          filterPanel: {
                            filters: {
                              $set: [],
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'UPDATE_FILTER_IN_OVERRIDE': {
      const { propKey, filterId, operation, field } = action.payload;
      if (!propKey || !filterId) return state;
      if (['add', 'update'].includes(operation) && !field) return state;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) return state;

      if (!currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow])
        return state;
      const flows =
        currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow];
      if (!flows[0]?.flowOverride) return state;
      const activeFlow = currentCalculationSession.activeFlow;
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      flowOverride: {
                        filter: {
                          filters: {
                            $apply: (currFilters: IFlowOverrideField[]) => {
                              if (operation === 'add') {
                                return [...currFilters, field] as IFlowOverrideField[];
                              } else if (operation === 'update')
                                return currFilters.map((f) =>
                                  f.uid === filterId ? ({ ...field } as IFlowOverrideField) : f,
                                );
                              else if (operation === 'rmv') {
                                return currFilters.filter((f) => f.uid !== filterId);
                              } else return currFilters;
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'UPDATE_OVERRIDE_FILTER_PANEL': {
      const { propKey, operation, filterId, filterInfo } = action.payload;

      if (!propKey || !operation || !filterId) return state;
      if (['add', 'update'].includes(operation) && !filterInfo) return state;

      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.activeFlow) return state;

      if (!currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow])
        return state;
      const flows =
        currentCalculationSession.calculationInfo.flows[currentCalculationSession.activeFlow];
      if (!flows[0]?.flowOverride) return state;
      const activeFlow = currentCalculationSession.activeFlow;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      flowOverride: {
                        filter: {
                          filterPanel: {
                            filters: {
                              $apply: (currFilter: IOverrideFilterInfo[]) => {
                                if (operation === 'add') return [...currFilter, filterInfo];
                                else if (operation === 'update')
                                  return currFilter.map((filter) =>
                                    filter.filterId === filterId ? filterInfo : filter,
                                  );
                                else
                                  return currFilter.filter(
                                    (filter) => filter.filterId !== filterId,
                                  );
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    /*
      calculations if the calculation is connected with parameter
      
    */

    case 'SET_PARAMETER_FOR_FLOW': {
      const { propKey, activeFlow, isFlowParameterized } = action.payload;

      if (!propKey || !activeFlow) return state;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              flowParameters: {
                [activeFlow]: { $set: isFlowParameterized },
              },
            },
          },
        },
      });
    }

    case 'SET_PARAMETER_ID': {
      const { propKey, parameterId, index } = action.payload;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;
      if (!currentCalculationSession) return state;

      const activeFlow = currentCalculationSession.activeFlow;
      if (!activeFlow) return state;

      const parameter = { parameterId, index };

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      parameter: {
                        $push: [parameter],
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'REMOVE_PARAMETER': {
      const { propKey, parameterId, calculationFlowUID } = action.payload;

      if (!parameterId) return state;
      if (!calculationFlowUID) return state;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    0: {
                      parameter: {
                        $apply: (parameter: any) => {
                          return parameter.filter(
                            (param: any) => param.parameterId !== parameterId,
                          );
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'UPDATE_PARAMETER_CARD_POSITION': {
      const { propKey, calculationFlowUID, parameterId, index } = action.payload;

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlowUID]: {
                    0: {
                      parameter: {
                        $apply: (parameter: any) => {
                          return parameter.map((param: any) => {
                            if (param.parameterId === parameterId) {
                              return {
                                ...param,
                                index,
                              };
                            }
                            return param;
                          });
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    case 'UPDATE_CALCULATION': {
      const { calculation } = action.payload;
      if (!calculation) return state;

      return update(state, {
        savedCalculations: {
          $apply: (calculations: any) => {
            const newCalculations = calculations.filter(
              (cal: any) => cal.uuid !== calculation.uuid,
            );
            return [...newCalculations, calculation];
          },
        },
        fetchRowLevelCalculations: {
          $set: !calculation.isAggregated,
        },
      });
    }

    case 'SET_PARAMETER_RIGHT_OPERAND': {
      const { propKey, conditionFilterId, filterIdx, rightOperand, rightOperandType, parameterId } =
        action.payload;

      if (!propKey || !conditionFilterId || filterIdx === undefined) return state;
      if (!parameterId) return state;

      if (!state.properties[propKey].currentCalculationSession) return state;

      const conditionFilter =
        state.properties[propKey].currentCalculationSession.calculationInfo.conditionFilters[
          conditionFilterId
        ][0].conditions[filterIdx];
      if (!conditionFilter) return state;

      let operator = conditionFilter.operator;

      if (operator === 'between') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [filterIdx]: {
                            rightOperand: {
                              $set: rightOperand,
                            },
                            rightOperandType: {
                              $set: [rightOperandType, rightOperandType],
                            },
                            parameterId: { $set: [parameterId] },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      } else {
        let currentRightOperand = conditionFilter.rightOperand || [];
        let currentRightOperandType = conditionFilter.rightOperandType || [];
        let currentParameterId = conditionFilter.parameterId || [];

        let source1 = [...currentRightOperand];
        let dataType1 = [...currentRightOperandType];
        let paramId: any = [...currentParameterId];

        let updateIndex = -1;

        if (source1[0] === null) {
          source1[0] = rightOperand;
          updateIndex = 0;
        } else if (source1[source1.length - 1] === null) {
          source1[source1.length - 1] = rightOperand;
          updateIndex = source1.length - 1;
        } else {
          source1.push(rightOperand);
          updateIndex = source1.length - 1;
        }

        if (dataType1[0] === 'blank') {
          dataType1[0] = rightOperandType;
        } else if (dataType1[dataType1.length - 1] === 'blank') {
          dataType1[dataType1.length - 1] = rightOperandType;
        } else {
          dataType1.push(rightOperandType);
        }

        if (!paramId.find((id: any) => id === parameterId)) {
          if (paramId.length === 0 && updateIndex > 0) {
            paramId[0] = null;
          }

          while (paramId.length <= updateIndex) {
            paramId.push(null);
          }

          paramId[updateIndex] = parameterId;
        }

        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  conditionFilters: {
                    [conditionFilterId]: {
                      0: {
                        conditions: {
                          [filterIdx]: {
                            rightOperand: {
                              $set: source1,
                            },
                            rightOperandType: {
                              $set: dataType1,
                            },
                            parameterId: { $set: paramId },
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }
    }

    case 'REMOVE_PARAMETER_RIGHT_OPERAND': {
      const { propKey, conditionFilterId, filterIdx, parameterId = '' } = action.payload;

      if (!propKey || !conditionFilterId || filterIdx === undefined) return state;

      if (!state.properties[propKey].currentCalculationSession) return state;
      const conditionFilter =
        state.properties[propKey].currentCalculationSession.calculationInfo.conditionFilters[
          conditionFilterId
        ][0].conditions[filterIdx];
      if (!conditionFilter) return state;

      let paramId: any[] = [];

      const isBetween = conditionFilter.operator === 'between';

      const paramIdArray = conditionFilter.parameterId;

      let rightOperand = conditionFilter.rightOperand || [];

      let rightOperandType = conditionFilter.rightOperandType || [];

      if (isBetween) {
        rightOperand = [];
        rightOperandType = [];
        paramId = [];
        // paramId = paramIdArray.map((id: string, index: number) => (id === parameterId ? null : id));
        // rightOperand = rightOperand.map((id: any, index: number) =>
        //   index === paramIdArray.indexOf(parameterId) ? null : id,
        // );
        // rightOperandType = rightOperandType.map((id: any, index: number) =>
        //   index === paramIdArray.indexOf(parameterId) ? 'blank' : id,
        // );
      }

      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                conditionFilters: {
                  [conditionFilterId]: {
                    0: {
                      conditions: {
                        [filterIdx]: {
                          rightOperand: {
                            $set: isBetween ? rightOperand : [null],
                          },
                          rightOperandType: {
                            $set: isBetween ? rightOperandType : ['blank'],
                          },
                          parameterId: { $set: paramId },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }
    case 'ADD_WINDOW_FUNCTION': {
      const { propKey, windowFunction ,flowId} = action.payload;
      if (!propKey || !windowFunction||!flowId) return state;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession ) {
        return state;
      }

      const override =
        currentCalculationSession.calculationInfo.flows[flowId]?.[0]?.flowOverride;
      const hasOverride = !!override;
      const overrideCount = currentCalculationSession.calculationInfo.overrideCount;
      if (!hasOverride) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [flowId]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          $set: {
                            windowFunction: windowFunction,
                            filter: {
                              type: 'honorAll',
                              isFilterGroup: false,
                              filterGroups: [],
                              filters: [] as IFlowOverrideField[],
                              filterPanel: {
                                panelName: 'overrideFilter',
                                shouldAllConditionsMatch: true,
                                filters: [],
                              },
                            },
                            dimension: {
                              type: 'honorAll',
                              dimensions: [] as IFlowOverrideField[],
                            },
                          },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $set: overrideCount + 1,
                  },
                },
              },
            },
          },
        });
      } else {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [flowId]: {
                      0: {
                        overrided: { $set: true },
                        flowOverride: {
                          windowFunction: { $set: windowFunction },
                        },
                      },
                    },
                  },
                  overrideCount: {
                    $set: overrideCount + (override.windowFunction ? 0 : 1),
                  },
                },
              },
            },
          },
        });
      }
    }
    case 'REMOVE_WINDOW_FUNCTION': {
      const { propKey,flowId } = action.payload;
      if (!propKey || !flowId) return state;
      const currentCalculationSession = state.properties[propKey]?.currentCalculationSession;
      if (!currentCalculationSession) {
        return state;
      }

      const activeFlow = flowId;

      const hasOverride =
        !!currentCalculationSession.calculationInfo.flows[activeFlow]?.[0]?.flowOverride;
      const overrideCount = currentCalculationSession.calculationInfo.overrideCount;
      if (!hasOverride) {
        return state;
      }
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [activeFlow]: {
                    0: {
                      overrided: { $set: true },
                      flowOverride: {
                        windowFunction: { $set: null },
                      },
                    },
                  },
                },
                overrideCount: {
                  $set: overrideCount - 1 < 0 ? 0 : overrideCount - 1,
                },
              },
            },
          },
        },
      });
    }

    case 'DELETE_INVALID_FIELD': {
      const sourceID = action.payload.sourceUID;
      const propKey = action.payload.propKey;

      const currentCalculationSession = state.properties[propKey].currentCalculationSession;

      if (!currentCalculationSession || !currentCalculationSession.calculationInfo) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                fields: {
                  $apply: (fields: any) => {
                    const newFields = fields;
                    delete newFields[sourceID];
                    return newFields;
                  },
                },
              },
            },
          },
        },
      });

      return newState;
    }

    case 'REPLACE_SOURCE_INFO_IN_CALCULATION_FLOW': {
      const replaceIndex = action.payload.replaceIndex;
      const sourceId = action.payload.sourceId;
      const dataType = action.payload.dataType;
      const calculationFlow = action.payload.calculationFlow;

      const aggregation = action.payload.aggregation;
      const propKey = action.payload.propKey;
      const conditionNumber = action.payload.conditionNumber;

      const calculationSession = state.properties[propKey].currentCalculationSession;

      if (!calculationSession || !calculationSession.calculationInfo) return state;

      // index where replacement should happen; fallback to 0 if not provided
      const idx = replaceIndex == null ? 0 : replaceIndex;

      // flow type: replace (field)
      if (calculationFlow.flow === 'replace' && dataType === 'field') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // flow type: split (field)
      if (calculationFlow.flow === 'split' && dataType === 'field') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // substringleft / substringright (field)
      if (
        (calculationFlow.flow === 'substringleft' || calculationFlow.flow === 'substringright') &&
        dataType === 'field'
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // concat (text) — replace element at idx instead of push
      if (calculationFlow.flow === 'concat' && dataType === 'text') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // log flow — keep existing check; replace at idx when decimal base
      if (
        calculationFlow.flow === 'log' &&
        calculationSession.calculationInfo.flows[calculationFlow.uid][0].sourceType[0] === 'decimal'
      ) {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      0: {
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // numeric types: decimal, integer, binary
      if (dataType === 'decimal' || dataType === 'integer' || dataType === 'binary') {
        return update(state, {
          properties: {
            [propKey]: {
              currentCalculationSession: {
                calculationInfo: {
                  flows: {
                    [calculationFlow.uid]: {
                      // replace inside the requested condition step (or 0)
                      [conditionNumber === null ? 0 : conditionNumber]: {
                        sourceType: {
                          [idx]: { $set: dataType },
                        },
                        source: {
                          [idx]: { $set: sourceId },
                        },
                        aggregation: {
                          [idx]: { $set: aggregation },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      // default: text / other types — replace at idx inside the given condition step
      return update(state, {
        properties: {
          [propKey]: {
            currentCalculationSession: {
              calculationInfo: {
                flows: {
                  [calculationFlow.uid]: {
                    [conditionNumber === null ? 0 : conditionNumber]: {
                      sourceType: {
                        [idx]: { $set: dataType },
                      },
                      source: {
                        [idx]: { $set: sourceId },
                      },
                      aggregation: {
                        [idx]: { $set: aggregation },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      });
    }

    default:
      return state;
  }
};

export default CalculationReducer;
